import { usePaymentMethodsApi } from "@/Hooks/usePaymentMethodsApi";
import CreditCardsCardEntryForm from "@/Shared/CreditCards/Forms/CreditCardsCardEntryForm";
import CreditCardsStoreButton from "@/Shared/CreditCards/Buttons/CreditCardsStoreButton";
import useStoreCreditCard from "@/Features/CreditCards/Exports/Hooks/useCreditCardsStore";
import { useState } from "react";
import CreditCardsAddLegalease from "@/Shared/CreditCards/CustomComponents/CreditCardsAddLegalease";
import { useAuthentication } from "@/Slices/authenticationSlice";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";

interface AddCreditCardDialogProps {
  onCardAdded: () => void;
  onCancel: () => void;
}

const CreditCardsAddCreditCardDialog: React.FC<AddCreditCardDialogProps> = (
  props: AddCreditCardDialogProps,
) => {
  const {
    storeCreditCard,
    billingAddress,
    billingName,
    setBillingName,
    setBillingAddress,
    errorMessageForBillingName,
    errorMessageForCreditCardNumber,
    errorMessageForCreditCardExpiration,
    errorMessageForCreditCardCVC,
    setErrorMessageForCreditCardNumber,
    setErrorMessageForCreditCardExpiration,
    setErrorMessageForCreditCardCVC,
    errorMessageForAddress,
    errorMessageForCity,
    errorMessageForStateOrProvinceCode,
    locationsErrorMessageForPostalCode,
    errorMessageForCountry,
    isValid,
  } = useStoreCreditCard();
  const paymentMethodsApi = usePaymentMethodsApi();

  const [storingInProgress, setStoringInProgress] = useState(false);
  const [storingSuccess, setStoringSuccess] = useState(false);
  const [storingFailure, setStoringFailure] = useState(false);
  const [failureMessage, setFailureMessage] = useState<string>();

  const [validated, setValidated] = useState(false);
  const { user } = useAuthentication();

  async function onStoreCreditCard() {
    setValidated(true);

    if (!isValid) {
      return;
    }

    const saveCardIntentResponse =
      await paymentMethodsApi.generateSaveCardIntent();

    const clientSecret = saveCardIntentResponse.clientSecret;

    setStoringSuccess(false);
    setStoringFailure(false);
    setStoringInProgress(true);

    const result = await storeCreditCard(clientSecret);

    setStoringInProgress(false);

    if (result.setupIntent) {
      setStoringSuccess(true);
      if (user?.companyId) {
        await paymentMethodsApi.onPaymentMethodAdded({
          onPaymentMethodAddedRequest: {
            companyId: user.companyId,
          },
        });
      }
      props.onCardAdded();
    } else {
      setStoringFailure(true);
      setFailureMessage(result.error.message);
    }
  }

  // Customer-facing, so underlined like the rest of the customer flows.
  return (
    <ControlPresentationProvider presentation="underlined">
      {/* No heading here: the wrapping Dialog owns `title` and `description`,
          which is what every other dialog uses and what keeps the header at the
          dialog scale. Rendering a `2xl` bold line in the body made this one
          header noticeably larger than the rest of the app's. */}
      <Stack gap={8}>
        <CreditCardsCardEntryForm
          addresses={[]}
          billingAddress={billingAddress}
          billingName={billingName}
          setBillingAddress={setBillingAddress}
          setBillingName={setBillingName}
          forceValidation={validated}
          errorMessageForBillingName={errorMessageForBillingName}
          errorMessageForCreditCardNumber={errorMessageForCreditCardNumber}
          errorMessageForCreditCardExpiration={
            errorMessageForCreditCardExpiration
          }
          errorMessageForCreditCardCVC={errorMessageForCreditCardCVC}
          errorMessageForAddress={errorMessageForAddress}
          errorMessageForCity={errorMessageForCity}
          errorMessageForStateOrProvinceCode={
            errorMessageForStateOrProvinceCode
          }
          locationsErrorMessageForPostalCode={
            locationsErrorMessageForPostalCode
          }
          errorMessageForCountry={errorMessageForCountry}
          onCreditCardCVCError={setErrorMessageForCreditCardCVC}
          onCreditCardExpirationError={setErrorMessageForCreditCardExpiration}
          onCreditCardNumberError={setErrorMessageForCreditCardNumber}
          onValidate={() => setValidated(true)}
          sideBySideLayout
        />
        <Stack gap={2}>
          <CreditCardsAddLegalease />
          <Row gap={4} justify="end">
            {!storingInProgress && !storingSuccess && (
              <Button size="link" inheritFont onClick={props.onCancel}>
                Cancel
              </Button>
            )}
            <CreditCardsStoreButton
              onClick={onStoreCreditCard}
              storingInProgress={storingInProgress}
              storingSuccess={storingSuccess}
              storingFailure={storingFailure}
              failureMessage={failureMessage}
            />
          </Row>
        </Stack>
      </Stack>
    </ControlPresentationProvider>
  );
};

export default CreditCardsAddCreditCardDialog;
